import { useContext, useState, useEffect } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import useBilling from "../../services/Billing/useBilling";
import { GlobalContext } from "../../Context/GlobalContext";
import { useNavigate } from "react-router-dom";
import LoadingScreen from "../../utils/LoadingScreen";
import PaginationComponent from "../../utils/TablePagination";
import NoIndex from "../../components/NoIndex";
import { RotateCcw } from "lucide-react";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
);

const Billing = () => {
  const [interval, setInterval] = useState("30");
  const [ledgerPage, setLedgerPage] = useState(1);
  const LEDGER_PER_PAGE = 5;
  const { userID } = useContext(GlobalContext);
  const navigate = useNavigate();

  // Auto fetch ledger data on page load
  useEffect(() => {
    fetchLedgerData();
  }, []);

  const {
    walletBalance,
    ledgerData,
    loading,
    billingData,
    startDateCurrent,
    endDateCurrent,
    setStartDateCurrent,
    setEndDateCurrent,
    fetchLedgerData,
  } = useBilling(userID, interval);

  const totalCost = billingData?.totalCost ?? "0";
  const averageCost = billingData?.averageCost ?? "0";
  const totalMessages = billingData?.totalMessages ?? "0";
  const successRate = billingData?.successRate ?? "0%";
  const campaigns = billingData?.campaignWise ?? [];

  const handleRefresh = async () => {
    await fetchLedgerData();
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#9ca3af", font: { size: 12 } },
      },
      y: { display: false, grid: { display: false } },
    },
  };

  const barData = {
    labels: campaigns.map((c) => c.campaignName),
    datasets: [
      {
        data: campaigns.map((c) => parseFloat(c.totalAmoutSpent || 0)),
        backgroundColor: "#3fa55b",
        borderRadius: 3,
        barThickness: 30,
      },
    ],
  };

  const totalDelivered = campaigns.reduce(
    (sum, c) => sum + Number(c.totalDelivered || 0),
    0,
  );
  const totalRead = campaigns.reduce(
    (sum, c) => sum + Number(c.totalRead || 0),
    0,
  );
  const totalFailed = campaigns.reduce(
    (sum, c) => sum + Number(c.failed || 0),
    0,
  );

  const doughnutData = {
    datasets: [
      {
        data: [totalDelivered, totalRead, totalFailed],
        backgroundColor: ["#3fa55b", "#d1d5db", "#e5e7eb"],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
  };

  return (
    <div className="min-h-0 h-full flex flex-col overflow-hidden p-6 font-sans">
      <NoIndex />
      
      {/* ── HEADER ── */}
    <div className="flex justify-between items-center mb-6 montserrat-font-1 flex-shrink-0">
        <h2 className="text-2xl font-semibold text-gray-800 montserrat-font-1">
          Billing & Ledger
        </h2>

        <div className="flex items-center gap-3">
          {/* Existing wallet box */}
          <div className="flex items-stretch rounded-lg border border-[#2e8b57] overflow-hidden">
            {/* LEFT — white dropdown with down arrow */}

            <div className="relative flex items-center bg-white">
              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                className="appearance-none bg-white text-gray-700 text-sm pl-3 pr-7 py-2 outline-none cursor-pointer"
              >
                <option value="30">Last 30 Days</option>
                <option value="7">Last 7 Days</option>
                <option value="90">Last 90 Days</option>
              </select>
              <div className="pointer-events-none absolute right-2">
                <svg
                  className="w-3.5 h-3.5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* RIGHT — solid green wallet balance */}
            <div className="bg-[#2e8b57] flex items-center px-4 py-2">
              <span className="text-white font-bold text-sm whitespace-nowrap">
                Wallet Balance : ₹{" "}
                {walletBalance !== null ? walletBalance.toFixed(2) : "0.00"}
              </span>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="border px-3 py-2 rounded-lg text-white text-sm bg-green-600 items-center flex gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

<div className="flex-1 overflow-y-auto min-h-0 pr-2">
      {/* ── MAIN GRID : Left col fully independent of right col ── */}
      {loading && (
        <div className="flex justify-center py-4">
          <LoadingScreen text="Loading..." />
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px",
          alignItems: "start",
        }}
      >
        {/* ── LEFT COL — cards + graph stacked, never affected by right col ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Amount Spent + Cost Per Message */}
          <div style={{ display: "flex", gap: "20px" }}>
            <div
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
              style={{ flex: 1 }}
            >
              <p className="text-gray-500 text-sm mb-1 montserrat-font-2">
                Amount Spent
              </p>
              <h3 className="text-4xl font-bold text-gray-800 montserrat-font-1">
                ₹ {parseFloat(totalCost).toFixed(2)}
              </h3>
              <p className="text-gray-500 text-sm mt-1 montserrat-font-2">
                In Campaign
              </p>
              <p className="text-green-600 font-semibold mt-2 text-sm montserrat-font-2">
                +{totalMessages} Messages
              </p>
            </div>

            <div
              className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
              style={{ flex: 1 }}
            >
              <p className="text-gray-500 text-sm mb-1 montserrat-font-2">
                Cost Per Message
              </p>
              <h3 className="text-4xl font-bold text-gray-800 montserrat-font-2">
                ₹ {parseFloat(averageCost || 0).toFixed(2)}
              </h3>
              <p className="text-gray-500 text-sm mt-1 montserrat-font-2">
                For Marketing Messages
              </p>
            </div>
          </div>

          {/* Graph Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center">
              <div className="flex flex-col flex-1">
                <h3 className="text-[#2e8b57] text-3xl font-bold montserrat-font-1">
                  {successRate}
                </h3>
                <p className="text-gray-500 text-sm mt-1 mb-4 montserrat-font-2">
                  Overall Campaign Success Rate
                </p>
                <div style={{ height: "180px", width: "85%" }}>
                  <Bar data={barData} options={barOptions} />
                </div>
              </div>
              <div style={{ width: "220px", height: "280px", flexShrink: 0 }}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </div>
            </div>
          </div>

          {/* How to Manage Your Billing */}
          <div className="bg-green-100 rounded-xl p-5 border-l-4 border-green-500">
            <h4 className="text-green-600 font-semibold mb-3 montserrat-font-1">
              How to Manage Your Billing
            </h4>
            <ul className="list-disc pl-5 text-gray-700 text-sm space-y-2 montserrat-font-2">
              <li className="montserrat-font-2">
                <strong>Top-up Wallet :</strong> Ensure sufficient balance for
                uninterrupted ad campaigns.
              </li>
              <li className="montserrat-font-2">
                <strong >Transaction Ledger :</strong> Monitor all credits and
                debits.
              </li>
            </ul>
          </div>

          {/* Last 5 Campaign Costs */}
          <div className="bg-white rounded-xl border shadow-sm p-5 border-l-4 border-green-500">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 montserrat-font-1">
              Last 5 Campaign Costs
            </h3>

            {campaigns.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4 montserrat-font-2">
                No campaign data found
              </p>
            ) : (
              <div className="space-y-3">
                {campaigns.slice(0, 5).map((c) => (
                  <div
                    key={c.id}
                    className="rounded-lg border border-gray-200 px-4 py-3"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-[#3fa55b] font-semibold text-sm">
                        {c.campaignName}
                      </span>
                      <span className="text-red-500 font-semibold text-sm">
                        ₹ {parseFloat(c.totalAmoutSpent || 0).toFixed(2)}
                      </span>
                    </div>
                    <hr className="my-2 border-gray-200" />
                    <p className="text-gray-600 text-xs montserrat-font-2">
                      Sent:{" "}
                      <strong className="text-gray-800">
                        {c.totalMessagesShoot}
                      </strong>
                      {" | "}
                      Delivered:{" "}
                      <strong className="text-gray-800">
                        {c.totalDelivered}
                      </strong>
                      {" | "}
                      Read:{" "}
                      <strong className="text-gray-800">{c.totalRead}</strong>
                      {" | "}
                      Failed:{" "}
                      <strong className="text-red-500">{c.failed}</strong>
                      {" | "}
                      Success Rate:{" "}
                      <strong className="text-gray-800">{c.successRate}</strong>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT COL — Transaction Ledger only, grows/shrinks independently ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-[7px] flex flex-col">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 montserrat-font-1">
            Transaction Ledger
          </h3>

          <div className="flex items-center gap-3 mb-4 flex-wrap montserrat-font-1">
            <input
              type="date"
              value={startDateCurrent}
              onChange={(e) => setStartDateCurrent(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-md text-sm bg-white outline-none"
            />
            <input
              type="date"
              value={endDateCurrent}
              onChange={(e) => setEndDateCurrent(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-md text-sm bg-white outline-none"
            />
            <button
              onClick={() => navigate("/Billing/Ledger")}
              className="bg-[#2e8b57] hover:bg-[#256e47] text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              View Full Ledger
            </button>
          </div>

          {ledgerData.length === 0 ? (
            <p className="text-gray-400 text-center text-sm pt-6 montserrat-font-2">
              No data found
            </p>
          ) : (
            <>
              <div className="space-y-3 montserrat-font-1">
                {ledgerData
                  .slice(
                    (ledgerPage - 1) * LEDGER_PER_PAGE,
                    ledgerPage * LEDGER_PER_PAGE,
                  )
                  .map((txn, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg border border-gray-200 px-4 py-3"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">
                          {new Date(txn.transactionDate).toLocaleString()}
                        </span>
                        <span className="text-gray-800 font-semibold text-sm">
                          {txn.narraton}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span
                          className={`text-sm font-semibold ${txn.transactionType === "credit" ? "text-green-600" : "text-red-500"}`}
                        >
                          Type:{" "}
                          {txn.transactionType.charAt(0).toUpperCase() +
                            txn.transactionType.slice(1)}
                        </span>
                        <span
                          className={`text-sm font-semibold ${txn.transactionType === "credit" ? "text-green-600" : "text-red-500"}`}
                        >
                          Amount: ₹{" "}
                          {parseFloat(txn.transactionAmount || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Pagination */}
              <PaginationComponent
                currentPage={ledgerPage}
                totalPages={Math.ceil(ledgerData.length / LEDGER_PER_PAGE)}
                onNextPage={() =>
                  setLedgerPage((p) =>
                    Math.min(
                      p + 1,
                      Math.ceil(ledgerData.length / LEDGER_PER_PAGE),
                    ),
                  )
                }
                onPrevPage={() => setLedgerPage((p) => Math.max(p - 1, 1))}
                onPageClick={(page) => setLedgerPage(page)}
              />
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Billing;
